import { createSelector } from '@reduxjs/toolkit'

import { CrackDetectionStage } from '../interfaces'
import { PolylineToolState } from './core'
import { getDetectionKind, hasDetectionResult } from './crackDetection'

/**
 * Select working points of the current area
 */
export const selectCrackDetectionWorkingPoints = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => executions[areaId]?.points || [],
)

/**
 * Select job token of the current area
 */
export const selectCrackDetectionJobToken = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => executions[areaId]?.jobToken,
)

/**
 * Temporary selector to get crack detection stage from current area.
 * To be removed once multi-crack detection per-area is implemented.
 */
export const selectCrackDetectionStage = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => executions[areaId]?.status || CrackDetectionStage.None,
)

/**
 * Select what the current area's detection run looks for.
 */
export const selectDetectionKind = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => getDetectionKind(executions[areaId]),
)

/**
 * Select the chalk detection colour pick of the current area, if one has been made.
 */
export const selectPointForTargetColor = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => executions[areaId]?.pointForTargetColor,
)

/**
 * Select the colour sampled under the current area's colour pick.
 */
export const selectTargetColorHex = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => executions[areaId]?.targetColorHex,
)

/**
 * Select whether the current area's detection run has produced a result.
 */
export const selectHasDetectionResult = createSelector(
  (state: PolylineToolState) => state.crackDetection,
  ({ areaId, executions }) => hasDetectionResult(executions[areaId]),
)

/**
 * Selector to determine if entire crack detection is dirty (has unsaved changes).
 */
export const selectCrackDetectionDirtyState = createSelector(
  [
    (state: PolylineToolState) => state.crackDetection,
    (state: PolylineToolState) => state.core,
    selectCrackDetectionStage,
  ],
  ({ areaId, executions }, tool, drawingStage) =>
    executions[areaId]?.points.length > 0 || (drawingStage === CrackDetectionStage.FineTune && !tool.isSaving),
)

/**
 * Selector to determine if crack detection is currently running.
 */
export const selectCrackDetectionLoadingState = createSelector(selectCrackDetectionStage, (drawingStage) =>
  [CrackDetectionStage.Requesting, CrackDetectionStage.Polling].includes(drawingStage),
)

/**
 * Selector to determine if polyline tool has unsaved changes.
 */
export const selectIsPolylineDirty = createSelector(
  [
    (state: PolylineToolState) => state.polyline.workingPoints.length,
    (state: PolylineToolState) => state.polyline.completedPolylines.length,
    (state: PolylineToolState) => state.polyline.editedPolylines.length,
  ],
  (workingPoints, completedPolylines, editedPolylines) =>
    workingPoints > 0 || completedPolylines > 0 || editedPolylines > 0,
)

/**
 * Selector to determine if polyline tool can perform undo action.
 * Basically the same as dirty state but without edited polylines
 * due to lack of support for comprehensive undo/redo stack.
 */
export const selectCanUndoPolyline = createSelector(
  [
    (state: PolylineToolState) => state.polyline.workingPoints.length,
    (state: PolylineToolState) => state.polyline.completedPolylines.length,
  ],
  (workingPoints, completedPolylines) => workingPoints > 0 || completedPolylines > 0,
)

/**
 * Select to determine whether user can change tools or not.
 * While crack is detection is running, only check for polyline's dirty state.
 * If there's no working polyline, check for crack detection's background job.
 */
export const selectCanChangePolylineTool = createSelector(
  [
    (state: PolylineToolState) => state.core.isSaving,
    (state: PolylineToolState) => state.crackDetection,
    selectIsPolylineDirty,
  ],
  (isSaving, { areaId, executions }, isDirty) => {
    // If polyline mode is dirty and not currently saving, definitely cannot change tools
    if (isDirty && !isSaving) {
      return false
    }

    // If there are working points in crack detection, it depends on the drawing stage.
    // That is, it can done during detecting stage or when saving polylines.
    if (executions[areaId]?.points.length > 0) {
      return [
        CrackDetectionStage.Requesting,
        CrackDetectionStage.Polling,
        CrackDetectionStage.SavingPolylines,
      ].includes(executions[areaId]?.status)
    }

    // Otherwise, can change tools
    return true
  },
)

/**
 * Select to determine whether area can be changed in the perspective of polyline tool.
 * It is mostly the same as `selectCanChangePolylineTool` but with additional checks;
 * disallow change if there are polylines being saved.
 */
export const selectCanChangeAreaForPolylineTool = createSelector(
  [
    (state: PolylineToolState) => state.crackDetection,
    (state: PolylineToolState) => state.polyline.savingPolylines,
    selectIsPolylineDirty,
  ],
  ({ areaId, executions }, savingPolylines, isPolylineDirty) => {
    // If polyline mode is dirty, definitely cannot change areas
    if (isPolylineDirty || savingPolylines.length > 0) {
      return false
    }

    // If there are working points in crack detection, it depends on the drawing stage.
    // That is, it can done during detecting stage or when saving polylines.
    if (executions[areaId]?.points.length > 0) {
      return [
        CrackDetectionStage.Requesting,
        CrackDetectionStage.Polling,
        CrackDetectionStage.SavingPolylines,
      ].includes(executions[areaId].status)
    }

    // Otherwise, can change areas
    return true
  },
)

/**
 * Select to determine the mode of submit button; save and exit or just exit.
 * - Have working points in polyline tool.
 * - Have completed polylines in polyline tool.
 * - Have crack detection result.
 */
export const selectIsSaveAndExitMode = createSelector(
  (state: PolylineToolState) => state.polyline.workingPoints.length,
  (state: PolylineToolState) => state.polyline.completedPolylines.length,
  (state: PolylineToolState) => state.polyline.editedPolylines.length,
  selectHasDetectionResult,
  (workingPoints, completedPolylines, editedPolylines, hasResult) =>
    workingPoints > 0 || completedPolylines > 0 || editedPolylines > 0 || hasResult,
)

/**
 * Select to determine whether user can save and exit polyline tool.
 */
export const selectCanSaveAndExitPolylineTool = createSelector(
  [
    (state: PolylineToolState) => state.polyline.workingPoints,
    (state: PolylineToolState) => state.polyline.completedPolylines,
    selectHasDetectionResult,
  ],
  (workingPoints, completedPolylines, hasResult) => {
    // If there are still working points in Polyline mode, cannot exit
    // If there is a detection result but no completed polylines, cannot exit
    if (workingPoints.length > 0 || (hasResult && completedPolylines.length === 0)) {
      return false
    }

    // Otherwise, can exit
    return true
  },
)
